import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export type NoteEditorColors = {
  bar: string;
  soft: string;
  ink: string;
};

export type NoteEditorProps = {
  value: string;
  onChange: (value: string) => void;
  onSave: () => void;
  onCancel?: () => void;
  isSaving?: boolean;
  saveDisabled?: boolean;
  colors?: NoteEditorColors;
  placeholder?: string;
};

/**
 * Constrained note editor layout. The textarea owns overflow; the action
 * footer remains visible at the bottom of the available editor height.
 */
export default function NoteEditor({
  value,
  onChange,
  onSave,
  onCancel,
  isSaving = false,
  saveDisabled = false,
  colors,
  placeholder = "Private notes for this task…",
}: NoteEditorProps) {
  const hasContent = value.trim().length > 0;
  const saveStyle = colors
    ? { background: colors.soft, color: colors.bar }
    : undefined;

  return (
    <div className="ui-note-editor task-note-editor">
      <div className="ui-note-editor__field note-field relative">
        <Textarea
          value={value}
          onChange={event => onChange(event.target.value)}
          placeholder={placeholder}
          className="ui-note-editor__input note-input pr-8"
          autoFocus
        />
        {hasContent && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent/40"
            aria-label="Clear note"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {onCancel ? (
        <div className="ui-note-editor__actions note-actions">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onSave}
            disabled={saveDisabled || isSaving}
            style={saveStyle}
          >
            Save
          </Button>
        </div>
      ) : (
        <Button
          type="button"
          className="ui-note-editor__actions h-12 w-full"
          onClick={onSave}
          disabled={isSaving}
          style={saveStyle}
        >
          Save
        </Button>
      )}
    </div>
  );
}
